import React from "react"
import PropTypes from "prop-types"

const sampleReplace = (text) => {
  if (!text) return ""
  return text
    .replace(/\{\{\s*1\s*\}\}/g, "John Doe")
    .replace(/\{\{\s*2\s*\}\}/g, "ORD-12345")
    .replace(/\{\{\s*3\s*\}\}/g, "Tomorrow 3PM")
}

function MediaHeader({ format, mediaUrl }) {
  if (!format || format === "TEXT") return null

  if (format === "IMAGE") {
    return (
      <div className="w-full overflow-hidden">
        {mediaUrl ? (
          <img
            src={mediaUrl}
            alt="Header image"
            className="w-full h-48 object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = e.target.nextElementSibling;
              if (fallback) {
                fallback.style.display = 'flex';
              }
            }}
          />
        ) : null}

        {/* Error fallback */}
        <div
          className="w-full h-48 bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center"
          style={{ display: mediaUrl ? 'none' : 'flex' }}
        >
          <div className="text-center p-4">
            <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-gray-500 font-medium">Add Image URL</p>
            <p className="text-xs text-gray-400">Enter an image URL to preview</p>
          </div>
        </div>
      </div>
    )
  }



  return null
}

const WhatsAppPreview = ({
  headerText,
  headerFormat,
  bodyText,
  footerText,
  buttons = [],
  mediaUrl = null,
}) => {
  const header = sampleReplace(headerText)
  const body = sampleReplace(bodyText)
  const footer = sampleReplace(footerText)

  return (
    <div
      className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden w-full max-w-sm mx-auto"
      style={{
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
      role="region"
      aria-label="WhatsApp message preview"
    >
      {/* Top bar (Business header) */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-green-500 rounded-full flex items-center justify-center shadow-sm border border-green-600">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">Your Business</div>
              <div className="text-xs text-gray-500 font-medium">✓ Verified Business</div>
            </div>
          </div>
          <div className="text-xs text-gray-400 font-medium" aria-label="timestamp">
            07:44
          </div>
        </div>
      </div>

      {/* Message container */}
      <div className="p-2 pb-3 bg-gray-100">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Header media/text */}
          <MediaHeader format={headerFormat} mediaUrl={mediaUrl} />

          {/* Header text for TEXT format or when provided with media */}
          {header && (
            <div
              className={`px-3 ${headerFormat && headerFormat !== "TEXT" ? "py-2 border-b border-gray-100" : "pt-3"}`}
            >
              <div className="font-bold text-gray-900 text-sm leading-tight">{header}</div>
            </div>
          )}

          {/* Body + Footer */}
          <div
            className={`px-3 ${!header && (!headerFormat || headerFormat === "TEXT" || !mediaUrl) ? "pt-3" : ""} pb-3`}
          >
            {body && (
              <div className="mb-3">
                <p className="text-xs text-gray-800 whitespace-pre-line leading-relaxed">{body}</p>
              </div>
            )}

            {footer && (
              <div className="mb-1">
                <p className="text-xs text-gray-500 italic font-medium">{footer}</p>
              </div>
            )}

            {/* Meta: time + read ticks */}
            <div className="flex items-center justify-end mt-2">
              <span className="text-xs text-gray-400 mr-2 font-medium">07:44</span>
              <div className="flex space-x-0.5" aria-label="read status">
                <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        {buttons && buttons.length > 0 && (
          <div className="mt-2 space-y-1.5" aria-label="template buttons">
            {buttons.map((button, index) => {
              const icon =
                button.type === "URL" ? (
                  <svg className="w-3 h-3 mr-1.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : button.type === "QUICK_REPLY" ? (
                  <svg className="w-3 h-3 mr-1.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg className="w-3 h-3 mr-1.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                )

              return (
                <button
                  key={`${button.text}-${index}`}
                  type="button"
                  className="w-full py-2 px-3 bg-white border border-gray-300 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-50 hover:border-blue-300 transition-colors duration-200 flex items-center justify-center shadow-sm active:scale-[0.98]"
                  aria-label={button.text}
                >
                  {icon}
                  <span className="">{button.text}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

WhatsAppPreview.propTypes = {
  headerText: PropTypes.string,
  headerFormat: PropTypes.oneOf(["TEXT", "IMAGE"]),
  bodyText: PropTypes.string,
  footerText: PropTypes.string,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      type: PropTypes.oneOf(["URL", "QUICK_REPLY", "PHONE_NUMBER"]),
      url: PropTypes.string,
    }),
  ),
  mediaUrl: PropTypes.string,
}

export default WhatsAppPreview
